/**
 * Product Hero Gallery content-modal behavior.
 *
 * Owns Gallery, Video, and Tour modal rendering, playback, dismissal, focus,
 * and history routing without initializing the public gallery runtime.
 */

(() => {
  const MODULE_KEY = '__MarineMaxProductHeroGalleryContentModals';

  if (window[MODULE_KEY]?.install) return;

  class ProductHeroGalleryContentModalsBehavior {
    initializeContentModalsState() {
      this.imagesData = [];
      this.renderedGalleryGrid = null;
      this.renderedGalleryImagesData = null;
    }

    escapeHtml(value = '') {
      return String(value)
        .replaceAll('&', '&amp;')
        .replaceAll('"', '&quot;')
        .replaceAll("'", '&#39;')
        .replaceAll('<', '&lt;')
        .replaceAll('>', '&gt;');
    }

    parseImagesData() {
      const dataEl = document.querySelector(`#GalleryModalImages-${this.sectionId}`);
      if (!dataEl) return;

      try {
        this.imagesData = JSON.parse(dataEl.textContent);
      } catch (error) {
        console.error('Error parsing gallery images data:', error);
      }
    }

    isMobileViewport() {
      return window.matchMedia(
        '(max-width: 767px), (pointer: coarse) and (max-width: 1024px) and (max-height: 600px)',
      ).matches;
    }

    setModalState(modal, isOpen) {
      if (!modal) return;
      modal.classList.toggle('is-open', isOpen);
      modal.setAttribute('aria-hidden', isOpen ? 'false' : 'true');

      if (isOpen) {
        document.body.style.overflow = 'hidden';
        return;
      }

      const hasOpenHeroUi =
        document.querySelector('.product__gallery-modal.is-open') ||
        document.querySelector('.product__lightbox.is-open') ||
        document.querySelector('.product__content-modal.is-open') ||
        document.querySelector('.product__media-hub-modal.is-open');
      document.body.style.overflow = hasOpenHeroUi ? 'hidden' : '';
    }

    openMediaExperience(kind) {
      if (this.mediaHubModal) {
        const nextView = kind === 'video' || kind === 'tour' ? kind : 'gallery';
        this.openMediaHubModal({
          view: nextView,
          galleryIndex: this.currentIndex,
          restoreFocusEl: document.activeElement,
        });
        return;
      }

      if (kind === 'gallery') {
        this.openGalleryModal();
        return;
      }
      if (kind === 'video') {
        this.openVideoModal();
        return;
      }
      if (kind === 'tour') this.openTourModal();
    }

    initGalleryModal() {
      this.galleryModal = document.querySelector(`#ProductGalleryModal-${this.sectionId}`);
      this.galleryBtn = this.heroBanner.querySelector('[data-media-open="gallery"]');
      this.heroGalleryInteractiveSelector =
        'button, a, input, textarea, select, summary, [role="button"], [data-hero-nav], [data-hero-dot], [data-media-open], [data-mobile-media-hub-open]';

      this.mediaActionButtons.forEach((button) => {
        button.addEventListener(
          'click',
          () => this.openMediaExperience(button.dataset.mediaOpen),
          { signal: this.eventAbortController.signal },
        );
      });

      this.mobileMediaHubBtn?.addEventListener(
        'click',
        () => this.openMediaHubModal(),
        { signal: this.eventAbortController.signal },
      );

      this.heroMedia?.addEventListener(
        'click',
        (event) => {
          if (event.defaultPrevented) return;
          if (event.target?.closest(this.heroGalleryInteractiveSelector)) return;
          if (this.mediaHubModal) {
            this.openMediaHubModal({
              view: 'gallery',
              galleryIndex: this.currentIndex,
              restoreFocusEl:
                this.mobileMediaHubBtn || this.galleryBtn || this.heroMedia,
            });
            return;
          }
          this.openGalleryModal();
        },
        { signal: this.eventAbortController.signal },
      );

      if (!this.galleryModal) return;

      this.galleryModal.querySelectorAll('[data-gallery-close]').forEach((button) => {
        button.addEventListener('click', () => this.closeGalleryModal(), {
          signal: this.eventAbortController.signal,
        });
      });

      const galleryContent = this.galleryModal.querySelector(
        '.product__gallery-modal-content',
      );
      galleryContent?.addEventListener(
        'click',
        (event) => {
          if (!event.target.closest('.product__gallery-modal-grid')) {
            this.closeGalleryModal();
          }
        },
        { signal: this.eventAbortController.signal },
      );

      this.galleryGrid = document.querySelector(`#GalleryModalGrid-${this.sectionId}`);
      this.galleryGrid?.addEventListener(
        'click',
        (event) => {
          const button = event.target?.closest?.('[data-gallery-open]');
          if (!button) return;
          const index = parseInt(button.dataset.galleryOpen || '0', 10);
          this.openLightbox(index, { source: 'gallery' });
        },
        { signal: this.eventAbortController.signal },
      );

      this.handleGalleryKeydown = (event) => {
        if (
          event.key === 'Escape' &&
          this.galleryModal.classList.contains('is-open')
        ) {
          this.closeGalleryModal();
        }
      };
      document.addEventListener('keydown', this.handleGalleryKeydown, {
        signal: this.eventAbortController.signal,
      });

    }

    openGalleryModal({ pushHistory = true, focus = true } = {}) {
      if (!this.galleryModal) return;
      this.renderGalleryImages();
      this.setModalState(this.galleryModal, true);

      if (pushHistory) {
        const currentPayload = this.getHistoryStatePayload();
        const { replace } = this.prepareHistoryForOverlay({
          allowReplaceCurrent: currentPayload?.overlay === 'modal',
        });
        this.setHistoryState(
          { overlay: 'modal', modalType: 'gallery' },
          { replace },
        );
      }

      const closeBtn = this.galleryModal.querySelector('[data-gallery-close]');
      if (focus && closeBtn) closeBtn.focus();
    }

    closeGalleryModal({ syncHistory = true } = {}) {
      if (!this.galleryModal) return;
      if (syncHistory) {
        this.navigateBackOrClose('modal', () =>
          this.closeGalleryModal({ syncHistory: false }),
        );
        return;
      }

      this.setModalState(this.galleryModal, false);
      this.syncCurrentHistoryEntryToUi();

      if (this.isMobileViewport() && this.mobileMediaHubBtn) {
        this.mobileMediaHubBtn.focus();
      } else if (this.galleryBtn) {
        this.galleryBtn.focus();
      }
    }

    bindContentModalDismissal(modal, closeModal) {
      if (!modal || typeof closeModal !== 'function') return;

      const listenerOptions = { signal: this.eventAbortController.signal };
      modal.querySelectorAll('[data-content-close]').forEach((button) => {
        button.addEventListener('click', closeModal, listenerOptions);
      });

      modal.addEventListener(
        'click',
        (event) => {
          if (event.target === modal) closeModal();
        },
        listenerOptions,
      );

      document.addEventListener(
        'keydown',
        (event) => {
          if (event.key === 'Escape' && modal.classList.contains('is-open')) {
            closeModal();
          }
        },
        listenerOptions,
      );
    }

    initVideoModal() {
      this.videoModal = document.querySelector(`#ProductVideoModal-${this.sectionId}`);
      if (!this.videoModal) return;

      this.videoModalFrame = this.videoModal.querySelector('[data-video-frame]');
      this.videoModalNative = this.videoModal.querySelector('[data-video-native]');
      this.videoModalUrl = (this.videoModal.dataset.videoUrl || '').trim();

      this.bindContentModalDismissal(this.videoModal, () => this.closeVideoModal());
    }

    setFrameSource(frame, url) {
      if (!frame || !url) return;
      if (frame.dataset.loadedSrc === url && frame.getAttribute('src') === url) return;
      frame.dataset.loadedSrc = url;
      frame.src = url;
    }

    pauseEmbeddedFrame(frame) {
      if (!frame) return;
      const src = frame.dataset.loadedSrc || frame.getAttribute('src') || '';
      if (!src) return;

      try {
        const targetOrigin = new URL(src, window.location.href).origin;
        if (src.includes('youtube.com/embed/')) {
          frame.contentWindow?.postMessage(
            JSON.stringify({
              event: 'command',
              func: 'pauseVideo',
              args: [],
            }),
            targetOrigin,
          );
          return;
        }

        if (src.includes('player.vimeo.com/video/')) {
          frame.contentWindow?.postMessage(
            JSON.stringify({ method: 'pause' }),
            targetOrigin,
          );
        }
      } catch (_) {}
    }

    stopVideoModalPlayback() {
      if (this.videoModalFrame) {
        this.pauseEmbeddedFrame(this.videoModalFrame);
        this.videoModalFrame.hidden = true;
      }
      if (this.videoModalNative) {
        this.videoModalNative.pause();
        this.videoModalNative.removeAttribute('src');
        this.videoModalNative.load();
        this.videoModalNative.hidden = true;
      }
    }

    openVideoModal({ pushHistory = true, focus = true } = {}) {
      if (!this.videoModal || !this.videoModalUrl) return;

      this.stopVideoModalPlayback();
      const embedUrl = this.getVideoEmbedUrl(this.videoModalUrl);
      if (embedUrl && this.videoModalFrame) {
        this.setFrameSource(this.videoModalFrame, embedUrl);
        this.videoModalFrame.hidden = false;
      } else if (this.videoModalNative) {
        this.videoModalNative.src = this.videoModalUrl;
        this.videoModalNative.hidden = false;
      }

      this.setModalState(this.videoModal, true);
      if (pushHistory) {
        const currentPayload = this.getHistoryStatePayload();
        const { replace } = this.prepareHistoryForOverlay({
          allowReplaceCurrent: currentPayload?.overlay === 'modal',
        });
        this.setHistoryState(
          { overlay: 'modal', modalType: 'video' },
          { replace },
        );
      }
      const closeBtn = this.videoModal.querySelector('[data-content-close]');
      if (focus && closeBtn) closeBtn.focus();
    }

    closeVideoModal({ syncHistory = true } = {}) {
      if (!this.videoModal) return;
      if (syncHistory) {
        this.navigateBackOrClose('modal', () =>
          this.closeVideoModal({ syncHistory: false }),
        );
        return;
      }
      this.stopVideoModalPlayback();
      this.setModalState(this.videoModal, false);
      this.syncCurrentHistoryEntryToUi();
      if (this.isMobileViewport() && this.mobileMediaHubBtn) {
        this.mobileMediaHubBtn.focus();
      }
    }

    initTourModal() {
      this.tourModal = document.querySelector(`#ProductTourModal-${this.sectionId}`);
      if (!this.tourModal) return;

      this.tourModalFrame = this.tourModal.querySelector('[data-tour-frame]');
      this.tourModalUrl = (this.tourModal.dataset.tourUrl || '').trim();

      this.bindContentModalDismissal(this.tourModal, () => this.closeTourModal());
    }

    stopTourModalPlayback() {
      if (this.tourModalFrame) {
        this.pauseEmbeddedFrame(this.tourModalFrame);
        this.tourModalFrame.hidden = true;
      }
    }

    openTourModal({ pushHistory = true, focus = true } = {}) {
      if (!this.tourModal || !this.tourModalUrl || !this.tourModalFrame) return;

      this.stopTourModalPlayback();
      this.setFrameSource(this.tourModalFrame, this.tourModalUrl);
      this.tourModalFrame.hidden = false;
      this.setModalState(this.tourModal, true);
      if (pushHistory) {
        const currentPayload = this.getHistoryStatePayload();
        const { replace } = this.prepareHistoryForOverlay({
          allowReplaceCurrent: currentPayload?.overlay === 'modal',
        });
        this.setHistoryState(
          { overlay: 'modal', modalType: 'tour' },
          { replace },
        );
      }

      const closeBtn = this.tourModal.querySelector('[data-content-close]');
      if (focus && closeBtn) closeBtn.focus();
    }

    closeTourModal({ syncHistory = true } = {}) {
      if (!this.tourModal) return;
      if (syncHistory) {
        this.navigateBackOrClose('modal', () =>
          this.closeTourModal({ syncHistory: false }),
        );
        return;
      }
      this.stopTourModalPlayback();
      this.setModalState(this.tourModal, false);
      this.syncCurrentHistoryEntryToUi();
      if (this.isMobileViewport() && this.mobileMediaHubBtn) {
        this.mobileMediaHubBtn.focus();
      }
    }

    renderGalleryImages() {
      const galleryGrid =
        this.galleryGrid ||
        document.querySelector(`#GalleryModalGrid-${this.sectionId}`);
      const dataEl = document.querySelector(`#GalleryModalImages-${this.sectionId}`);

      if (!galleryGrid || !dataEl) return;
      if (this.renderedGalleryGrid === galleryGrid && this.renderedGalleryImagesData === dataEl.textContent) return;

      try {
        const imagesData = JSON.parse(dataEl.textContent);
        let html = '';

        imagesData.forEach((image, index) => {
          const isLarge = index % 3 === 0;
          const width = Number(image.width) || '';
          const height = Number(image.height) || '';
          const aspectRatio =
            image.aspectRatio ||
            (width && height ? `${width} / ${height}` : '4 / 3');
          const sizes = isLarge
            ? '(max-width: 767px) calc(100vw - 32px), 758px'
            : '(max-width: 767px) calc((100vw - 44px) / 2), 371px';
          const alt = this.escapeHtml(image.alt || '');

          html += `
            <button
              type="button"
              class="product__gallery-modal-image${isLarge ? ' product__gallery-modal-image--large' : ''}"
              data-gallery-open="${index}"
              aria-label="Open gallery image ${index + 1}"
              style="--gallery-image-aspect-ratio: ${aspectRatio};"
            >
              <img
                src="${image.src}"
                srcset="${image.srcset || ''}"
                sizes="${sizes}"
                alt="${alt}"
                loading="lazy"
                decoding="async"
                ${width ? `width="${width}"` : ''}
                ${height ? `height="${height}"` : ''}
              >
            </button>
          `;
        });

        galleryGrid.innerHTML = html;
        this.renderedGalleryGrid = galleryGrid;
        this.renderedGalleryImagesData = dataEl.textContent;
      } catch (error) {
        console.error('Error rendering gallery images:', error);
      }
    }

    getVideoEmbedUrl(rawUrl) {
      if (!rawUrl) return null;
      try {
        const url = new URL(rawUrl, window.location.origin);
        const host = (url.hostname || '').toLowerCase();
        if (host.includes('youtu.be')) {
          const id = url.pathname.split('/').filter(Boolean).pop();
          return id
            ? `https://www.youtube.com/embed/${id}?enablejsapi=1&playsinline=1&rel=0`
            : null;
        }
        if (host.includes('youtube.com')) {
          const id = url.searchParams.get('v');
          return id
            ? `https://www.youtube.com/embed/${id}?enablejsapi=1&playsinline=1&rel=0`
            : null;
        }
        if (host.includes('vimeo.com')) {
          const id = url.pathname.split('/').filter(Boolean).pop();
          return id ? `https://player.vimeo.com/video/${id}?api=1` : null;
        }
      } catch (_) {}
      return null;
    }
  }

  const descriptors = Object.getOwnPropertyDescriptors(
    ProductHeroGalleryContentModalsBehavior.prototype,
  );
  delete descriptors.constructor;

  const install = (GalleryClass) => {
    if (typeof GalleryClass !== 'function') {
      throw new TypeError(
        'Product Hero Gallery content modals require a class constructor.',
      );
    }

    Object.defineProperties(GalleryClass.prototype, descriptors);
  };

  Object.defineProperty(window, MODULE_KEY, {
    configurable: true,
    enumerable: false,
    value: Object.freeze({ install }),
    writable: false,
  });
})();
